import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import toast from 'react-hot-toast'
import {
  Search,
  ArrowRight,
  ArrowLeft,
  Clock,
  Sparkles,
  BookOpen,
  Briefcase,
  X,
  Download,
  FileImage,
  Loader2,
  GraduationCap,
  CheckCircle2,
  Award,
  Phone,
  Share2,
  Check,
} from 'lucide-react'
import Reveal from '../components/landing/Reveal'
import {
  fetchCoursesByProgramType,
  getProgramTypeLabel,
  PROGRAM_TYPE_ORDER,
} from '../services/courseCatalogService'
import { exportCoursesTablePDF, exportCoursesTablePNG, exportSingleCoursePDF, exportSingleCoursePNG } from '../utils/coursePdfExport'

// ─── Program Type Tabs ─────────────────────────────────────────────────────
const PROGRAM_TABS = PROGRAM_TYPE_ORDER.map((value) => ({
  value,
  label: getProgramTypeLabel(value),
}))

export default function ProgramsPage() {
  const [searchParams] = useSearchParams()

  const initialProgramType = searchParams.get('type') || 'REGULAR'
  const initialCategory = searchParams.get('category') || 'All'
  const initialSearch = searchParams.get('search') || ''

  const [activeTab, setActiveTab] = useState(
    PROGRAM_TYPE_ORDER.includes(initialProgramType.toUpperCase())
      ? initialProgramType.toUpperCase()
      : 'REGULAR'
  )
  const [searchQuery, setSearchQuery] = useState(initialSearch)
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedProgram, setSelectedProgram] = useState(null)
  const [copiedCode, setCopiedCode] = useState(null)
  const [isDownloadingTable, setIsDownloadingTable] = useState(false)
  const [isDownloadingTablePNG, setIsDownloadingTablePNG] = useState(false)
  const [downloadingCourseCode, setDownloadingCourseCode] = useState(null)
  const [downloadingCoursePNGCode, setDownloadingCoursePNGCode] = useState(null)

  // Sync ?course= deep link
  useEffect(() => {
    const courseCode = searchParams.get('course')
    if (courseCode && courses.length > 0) {
      const match = courses.find(
        (c) => c.code.toLowerCase() === courseCode.toLowerCase()
      )
      if (match) setSelectedProgram(match)
    }
  }, [searchParams, courses])

  // Scroll to catalog on hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash !== 'programs-catalog') return undefined
    const scroll = () =>
      document.getElementById('programs-catalog')?.scrollIntoView({ behavior: 'auto', block: 'start' })
    const raf = window.requestAnimationFrame(scroll)
    const timer = window.setTimeout(scroll, 80)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(timer)
    }
  }, [])

  // Document title
  useEffect(() => {
    document.title =
      'All Programs & Curriculum | Synergy Multimedia Academy Kampala'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Explore all creative industry programs at Synergy Multimedia Academy in Kampala. Regular, Holiday, Bootcamp, and Corporate training across photography, film, design, animation, AI, and social media.'
      )
    }
  }, [])

  // Keyboard nav for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedProgram(null)
    }
    if (selectedProgram) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedProgram])

  // Fetch courses when tab changes
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setSelectedCategory('All')
    fetchCoursesByProgramType(activeTab)
      .then((data) => {
        if (!cancelled) {
          setCourses(data)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load programs.')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [activeTab])

  // Category list derived from loaded courses
  const categories = useMemo(() => {
    const set = new Set()
    courses.forEach((c) => { if (c.category) set.add(c.category) })
    return ['All', ...Array.from(set).sort()]
  }, [courses])

  const categoryCounts = useMemo(() => {
    const counts = { All: courses.length }
    courses.forEach((c) => {
      counts[c.category] = (counts[c.category] || 0) + 1
    })
    return counts
  }, [courses])

  // Filtered + sorted courses
  const filteredCourses = useMemo(() => {
    let result = courses.filter((c) => {
      if (selectedCategory !== 'All' && c.category !== selectedCategory) return false
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim()
        return (
          c.title.toLowerCase().includes(q) ||
          c.code.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q) ||
          (c.jobOpportunities || []).some((j) => j.toLowerCase().includes(q)) ||
          (c.advantages || []).some((a) => a.toLowerCase().includes(q)) ||
          (c.whatYouCanDo || []).some((w) => w.toLowerCase().includes(q))
        )
      }
      return true
    })
    return [...result].sort((a, b) => a.title.localeCompare(b.title))
  }, [courses, selectedCategory, searchQuery])

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleCopyLink = useCallback((code) => {
    const url = `${window.location.origin}/programs?course=${code}`
    navigator.clipboard.writeText(url).then(() => {
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 2000)
    })
  }, [])

  const handleDownloadTablePDF = useCallback(async () => {
    try {
      setIsDownloadingTable(true)
      const toastId = toast.loading('Generating programs directory PDF...')
      const fileName = await exportCoursesTablePDF(filteredCourses, {
        filterCategory: selectedCategory,
        searchQuery,
        programTypeLabel: getProgramTypeLabel(activeTab),
      })
      toast.success(`Downloaded ${fileName}`, { id: toastId })
    } catch (err) {
      console.error('PDF export failed:', err)
      toast.error('Failed to generate PDF directory. Please try again.')
    } finally {
      setIsDownloadingTable(false)
    }
  }, [filteredCourses, selectedCategory, searchQuery, activeTab])

  const handleDownloadSingleCoursePDF = useCallback(async (course) => {
    if (!course) return
    try {
      setDownloadingCourseCode(course.code)
      const toastId = toast.loading(`Generating ${course.code || course.title} PDF...`)
      const fileName = await exportSingleCoursePDF(course, {
        programTypeLabel: getProgramTypeLabel(activeTab),
      })
      toast.success(`Downloaded ${fileName}`, { id: toastId })
    } catch (err) {
      console.error('Single course PDF export failed:', err)
      toast.error(`Failed to generate ${course.code} PDF. Please try again.`)
    } finally {
      setDownloadingCourseCode(null)
    }
  }, [activeTab])

  const handleDownloadTablePNG = useCallback(async () => {
    try {
      setIsDownloadingTablePNG(true)
      const toastId = toast.loading('Generating programs directory PNG...')
      const fileName = await exportCoursesTablePNG(filteredCourses, {
        filterCategory: selectedCategory,
        searchQuery,
        programTypeLabel: getProgramTypeLabel(activeTab),
      })
      toast.success(`Downloaded ${fileName}`, { id: toastId })
    } catch (err) {
      console.error('PNG export failed:', err)
      toast.error('Failed to generate PNG directory. Please try again.')
    } finally {
      setIsDownloadingTablePNG(false)
    }
  }, [filteredCourses, selectedCategory, searchQuery, activeTab])

  const handleDownloadSingleCoursePNG = useCallback(async (course) => {
    if (!course) return
    try {
      setDownloadingCoursePNGCode(course.code)
      const toastId = toast.loading(`Generating ${course.code || course.title} PNG...`)
      const fileName = await exportSingleCoursePNG(course, {
        programTypeLabel: getProgramTypeLabel(activeTab),
      })
      toast.success(`Downloaded ${fileName}`, { id: toastId })
    } catch (err) {
      console.error('Single course PNG export failed:', err)
      toast.error(`Failed to generate ${course.code} PNG. Please try again.`)
    } finally {
      setDownloadingCoursePNGCode(null)
    }
  }, [activeTab])

  const resetFilters = () => {
    setSearchQuery('')
    setSelectedCategory('All')
  }

  const activeTabLabel = getProgramTypeLabel(activeTab)

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>All Programs & Curriculum | Synergy Multimedia Academy</title>
        <meta
          name="description"
          content="Explore all creative industry programs at Synergy Multimedia Academy in Kampala. Regular, Holiday, Bootcamp, and Corporate training."
        />
        <link rel="canonical" href="https://synergymultimediaacademy.com/programs" />
        <meta property="og:title" content="All Programs & Curriculum | Synergy Multimedia Academy" />
        <meta
          property="og:description"
          content="Explore all industry courses across Photography, Film, Graphic Design, Animation, AI, and Social Media at SMA."
        />
        <meta property="og:url" content="https://synergymultimediaacademy.com/programs" />
      </Helmet>

      <div className="cinema-grain" aria-hidden="true" />

      {/* Top Navigation */}
      <nav className="cinema-glass-strong sticky top-0 z-40 border-b border-white/5">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="flex justify-between items-center h-14 sm:h-20">
            <Link
              to="/explore"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2.5 sm:gap-3 group shrink-0"
            >
              <img
                src="/Logos/SMA-Logo-Dark.png"
                alt="Synergy Multimedia Academy logo"
                className="h-8 sm:h-12 transition-transform duration-500 group-hover:scale-105"
              />
              <div>
                <p className="font-display text-xs sm:text-sm tracking-tight leading-tight text-white">
                  Synergy Multimedia
                </p>
                <p className="text-xs uppercase tracking-[0.25em] text-cinema-gold font-medium">
                  Academy
                </p>
              </div>
            </Link>

            <div className="flex items-center gap-2.5 sm:gap-6">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 sm:gap-2 text-cinema-soft hover:text-cinema-gold text-[0.7rem] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-sans font-medium transition-colors group"
              >
                <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                <span className="hidden xs:inline sm:inline">Home</span>
              </Link>
              <Link
                to="/advisor"
                className="hidden lg:inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-cinema-muted hover:text-cinema-gold transition-colors font-sans"
              >
                <Phone size={14} />
                <span>Admissions Help</span>
              </Link>
              <Link
                to="/register"
                className="cinema-btn-primary !px-3.5 sm:!px-5 !py-2 sm:!py-2.5 !min-h-[38px] sm:!min-h-[42px] !text-xs"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Header — minimal */}
      <header className="relative pt-8 pb-8 sm:pt-14 sm:pb-12 overflow-hidden bg-gradient-to-b from-cinema-charcoal/80 via-cinema-black to-cinema-black border-b border-white/5">
        <div className="cinema-glow-orb w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] -top-20 sm:-top-40 right-4 sm:right-10 opacity-25 sm:opacity-35 pointer-events-none" />

        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <Reveal className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-xs uppercase tracking-[0.16em] font-sans font-semibold mb-4 sm:mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Programs Catalogue</span>
            </div>

            <h1 className="cinema-headline-xl text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-light tracking-tight">
              All <span className="text-cinema-gold">Programs</span>
            </h1>

            <p className="mt-3 sm:mt-5 cinema-body-lg text-xs sm:text-sm md:text-base text-cinema-soft/80 leading-relaxed max-w-2xl">
              Browse our full catalogue across Regular, Holiday, Bootcamp, and Corporate training.
              Download a directory PDF for any category, or open a program for its syllabus and career pathways.
            </p>
          </Reveal>
        </div>
      </header>

      {/* Main Content */}
      <main id="programs-catalog" className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-6 sm:py-10 scroll-mt-16 sm:scroll-mt-20">

        {/* ── Program Type Tabs ──────────────────────────────────────────── */}
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-3 no-scrollbar touch-pan-x mb-5 sm:mb-6">
          {PROGRAM_TABS.map((tab) => {
            const isActive = activeTab === tab.value
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-sm text-[0.7rem] sm:text-xs uppercase tracking-wider font-sans whitespace-nowrap transition-all duration-300 shrink-0 ${
                  isActive
                    ? 'bg-cinema-gold/15 text-cinema-gold border border-cinema-gold/40 font-semibold'
                    : 'bg-white/[0.02] text-cinema-muted hover:text-white border border-white/5 hover:border-white/20'
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* ── Search + Download Row ──────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-between items-stretch sm:items-center mb-5">
          <div className="relative flex-1 w-full sm:max-w-xl">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, code, category, career..."
              className="w-full pl-10 pr-10 py-2.5 sm:py-3 rounded-sm bg-white/[0.03] border border-white/10 text-white placeholder-cinema-muted/60 text-xs sm:text-sm font-sans focus:outline-none focus:border-cinema-gold/60 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cinema-muted hover:text-white p-1"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="text-xs font-sans font-semibold text-cinema-soft mr-0.5">Download</span>
            <button
              type="button"
              onClick={handleDownloadTablePDF}
              disabled={isDownloadingTable || filteredCourses.length === 0}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-sm text-xs font-sans font-medium text-cinema-gold bg-cinema-gold/10 hover:bg-cinema-gold/20 border border-cinema-gold/30 hover:border-cinema-gold/60 transition-all cursor-pointer shadow-sm disabled:opacity-50"
              title={`Download ${activeTabLabel} directory as PDF`}
              aria-label={`Download ${activeTabLabel} directory as PDF`}
            >
              {isDownloadingTable ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-cinema-gold" />
              ) : (
                <Download className="w-3.5 h-3.5 text-cinema-gold" />
              )}
              <span className="font-semibold">PDF</span>
            </button>
            <button
              type="button"
              onClick={handleDownloadTablePNG}
              disabled={isDownloadingTablePNG || filteredCourses.length === 0}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-sm text-xs font-sans font-medium text-cinema-soft hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all cursor-pointer shadow-sm disabled:opacity-50"
              title={`Download ${activeTabLabel} directory as PNG image`}
              aria-label={`Download ${activeTabLabel} directory as PNG image`}
            >
              {isDownloadingTablePNG ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-cinema-soft" />
              ) : (
                <FileImage className="w-3.5 h-3.5 text-cinema-soft" />
              )}
              <span className="font-semibold">PNG</span>
            </button>
          </div>
        </div>

        {/* ── Category Filter Pills ──────────────────────────────────────── */}
        {categories.length > 2 && (
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-3 no-scrollbar touch-pan-x mb-5">
            {categories.map((category) => {
              const count = categoryCounts[category] || 0
              const isActive = selectedCategory === category
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-sm text-[0.7rem] sm:text-xs uppercase tracking-wider font-sans whitespace-nowrap transition-all duration-300 flex items-center gap-1.5 sm:gap-2 shrink-0 ${
                    isActive
                      ? 'bg-cinema-gold/15 text-cinema-gold border border-cinema-gold/40 font-semibold'
                      : 'bg-white/[0.02] text-cinema-muted hover:text-white border border-white/5 hover:border-white/20'
                  }`}
                >
                  <span>{category}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      isActive
                        ? 'bg-cinema-gold text-black font-bold'
                        : 'bg-white/10 text-cinema-soft'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {/* ── Results Summary ────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-[0.7rem] sm:text-xs font-sans text-cinema-muted pt-2 border-t border-white/5 mb-5">
          <div className="flex items-center gap-2">
            <span>
              Showing <strong className="text-white">{filteredCourses.length}</strong> of{' '}
              {courses.length} {activeTabLabel.toLowerCase().includes('program') ? activeTabLabel.toLowerCase() : activeTabLabel.toLowerCase() + ' programs'}
            </span>
            {(selectedCategory !== 'All' || searchQuery) && (
              <button
                onClick={resetFilters}
                className="text-cinema-gold hover:underline ml-1 font-medium"
              >
                Reset filters
              </button>
            )}
          </div>
        </div>

        {/* ── Course List ────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cinema-gold" />
          </div>
        ) : error ? (
          <div className="max-w-lg mx-auto text-center py-16">
            <p className="text-sm text-red-400 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="cinema-btn-primary !px-6 !py-2.5 !text-xs"
            >
              Try Again
            </button>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="p-8 sm:p-16 text-center rounded-sm bg-white/[0.02] border border-white/5">
            <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-cinema-muted/40 mx-auto mb-4" />
            <h3 className="font-display text-lg sm:text-xl text-white font-light">No programs found</h3>
            <p className="text-xs sm:text-sm text-cinema-muted mt-2 max-w-md mx-auto font-sans">
              {searchQuery || selectedCategory !== 'All'
                ? 'Try adjusting your search or filters.'
                : `No ${activeTabLabel} are available right now. Check back soon or contact admissions.`}
            </p>
            {(searchQuery || selectedCategory !== 'All') && (
              <button onClick={resetFilters} className="mt-5 cinema-btn-ghost !text-xs !py-2.5">
                Reset filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2.5 sm:space-y-3">
            {filteredCourses.map((course, idx) => (
              <Reveal key={course.id || course.code} delay={Math.min(idx * 20, 200)}>
                <CourseListRow
                  course={course}
                  onView={() => setSelectedProgram(course)}
                  onDownload={() => handleDownloadSingleCoursePDF(course)}
                  isDownloading={downloadingCourseCode === course.code}
                  onDownloadPNG={() => handleDownloadSingleCoursePNG(course)}
                  isDownloadingPNG={downloadingCoursePNGCode === course.code}
                />
              </Reveal>
            ))}
          </div>
        )}

        {/* ── Admissions Callout ─────────────────────────────────────────── */}
        <section className="mt-10 sm:mt-16 p-5 sm:p-10 rounded-sm bg-gradient-to-r from-cinema-charcoal via-cinema-graphite to-cinema-charcoal border border-white/10 relative overflow-hidden">
          <div className="cinema-glow-orb w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] -right-10 sm:-right-20 -bottom-10 sm:-bottom-20 opacity-25 pointer-events-none" />
          <div className="relative z-10 grid lg:grid-cols-[1.5fr_1fr] gap-6 sm:gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-xs uppercase tracking-wider font-sans mb-3 sm:mb-4 font-semibold">
                <GraduationCap className="w-3.5 h-3.5" />
                <span>Admissions Support</span>
              </div>
              <h2 className="cinema-headline text-xl sm:text-3xl text-white font-light">
                Need Help Choosing the <span className="text-cinema-gold">Right Program?</span>
              </h2>
              <p className="mt-2.5 sm:mt-3 text-xs sm:text-sm text-cinema-soft/80 font-sans leading-relaxed max-w-xl">
                Our admissions counselors can evaluate your goals and schedule to recommend the ideal pathway.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-2.5 sm:gap-4">
                <Link to="/advisor" className="cinema-btn-primary !w-full sm:!w-auto !text-xs !py-3 flex items-center justify-center gap-2">
                  <span>Talk to an Advisor</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/showcase" className="cinema-btn-ghost !w-full sm:!w-auto !text-xs !py-3 flex items-center justify-center">
                  Browse Student Showcase
                </Link>
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-white/10 pt-5 lg:pt-0 lg:pl-8 space-y-2.5 font-sans text-xs text-cinema-muted">
              <p className="text-white font-medium text-xs sm:text-sm">Studio Advantages:</p>
              <p className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cinema-gold shrink-0" />
                Installment payment plans available
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cinema-gold shrink-0" />
                Industry equipment & AI licenses included
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cinema-gold shrink-0" />
                Accredited certification upon completion
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* ── Program Details Modal ────────────────────────────────────────── */}
      {selectedProgram && (
        <ProgramDetailModal
          course={selectedProgram}
          onClose={() => setSelectedProgram(null)}
          onDownload={() => handleDownloadSingleCoursePDF(selectedProgram)}
          isDownloading={downloadingCourseCode === selectedProgram.code}
          onDownloadPNG={() => handleDownloadSingleCoursePNG(selectedProgram)}
          isDownloadingPNG={downloadingCoursePNGCode === selectedProgram.code}
          onCopyLink={() => handleCopyLink(selectedProgram.code)}
          copied={copiedCode === selectedProgram.code}
        />
      )}

      {/* Footer */}
      <footer className="bg-cinema-black border-t border-white/5 py-10 mt-16">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <img
              src="/Logos/SMA-Logo-Dark.png"
              alt="SMA"
              className="h-8 opacity-80"
            />
            <p className="text-xs text-cinema-muted font-sans">
              &copy; {new Date().getFullYear()} Synergy Multimedia Academy. All rights reserved.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-xs text-cinema-muted font-sans">
            <Link to="/explore" className="hover:text-white transition-colors">Home</Link>
            <Link to="/showcase" className="hover:text-white transition-colors">Student Showcase</Link>
            <Link to="/advisor" className="hover:text-white transition-colors">Admissions Advisor</Link>
            <Link to="/register" className="text-cinema-gold hover:underline">Apply Now</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ─── Course List Row (minimalist) ──────────────────────────────────────────

function CourseListRow({ course, onView, onDownload, isDownloading, onDownloadPNG, isDownloadingPNG }) {
  return (
    <div
      onClick={onView}
      className="group flex items-center gap-3 sm:gap-5 px-4 sm:px-6 py-3.5 sm:py-4 rounded-sm bg-white/[0.02] border border-white/5 hover:border-cinema-gold/30 hover:bg-white/[0.04] transition-all duration-300 cursor-pointer"
    >
      {/* Code badge */}
      <div className="hidden sm:flex shrink-0 w-20 lg:w-24 justify-center">
        <span className="font-mono text-xs tracking-wider text-cinema-gold/80 px-2.5 py-1 bg-cinema-gold/5 border border-cinema-gold/15 rounded font-medium">
          {course.code || '—'}
        </span>
      </div>

      {/* Title + description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display text-sm sm:text-base text-white group-hover:text-cinema-gold transition-colors font-medium leading-tight">
            {course.title}
          </h3>
          {course.isNew && (
            <span className="px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider bg-cinema-gold/20 text-cinema-gold rounded-full font-bold">
              NEW
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-cinema-muted font-sans line-clamp-1 sm:line-clamp-2 leading-relaxed">
          {course.description}
        </p>
        {/* Mobile code + category row */}
        <div className="flex items-center gap-2 mt-1.5 sm:hidden">
          <span className="font-mono text-[0.65rem] text-cinema-gold/70">{course.code}</span>
          <span className="text-[0.65rem] text-cinema-muted">·</span>
          <span className="text-[0.65rem] text-cinema-soft/70">{course.category}</span>
        </div>
      </div>

      {/* Category — desktop */}
      <div className="hidden lg:block shrink-0 w-32">
        <span className="text-xs text-cinema-soft/70 font-sans">{course.category}</span>
      </div>

      {/* Duration */}
      <div className="hidden sm:flex shrink-0 items-center gap-1.5 w-24 lg:w-28">
        <Clock className="w-3.5 h-3.5 text-cinema-gold/60 shrink-0" />
        <span className="text-xs text-cinema-soft font-sans">{course.duration || '—'}</span>
      </div>

      {/* Fee */}
      <div className="shrink-0 w-24 sm:w-28 lg:w-32 text-right">
        <span className="font-display font-medium text-cinema-gold text-xs sm:text-sm tracking-tight">
          {course.fee || '—'}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        <span className="hidden sm:inline text-[0.7rem] font-sans font-semibold text-cinema-soft mr-0.5">Download</span>
        <button
          type="button"
          onClick={onDownload}
          disabled={isDownloading}
          className="p-2 rounded-sm text-xs text-cinema-soft hover:text-cinema-gold bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 transition-colors flex items-center justify-center min-h-[34px] min-w-[34px]"
          title={`Download ${course.code || course.title} PDF`}
          aria-label={`Download ${course.code} PDF`}
        >
          {isDownloading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-cinema-gold" />
          ) : (
            <Download className="w-3.5 h-3.5 text-cinema-gold/80" />
          )}
        </button>
        <button
          type="button"
          onClick={onDownloadPNG}
          disabled={isDownloadingPNG}
          className="p-2 rounded-sm text-xs text-cinema-soft hover:text-cinema-glow bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 transition-colors flex items-center justify-center min-h-[34px] min-w-[34px]"
          title={`Download ${course.code || course.title} PNG`}
          aria-label={`Download ${course.code} PNG`}
        >
          {isDownloadingPNG ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-cinema-glow" />
          ) : (
            <FileImage className="w-3.5 h-3.5 text-cinema-soft/80" />
          )}
        </button>
        <Link
          to={`/register?course=${course.code}`}
          className="px-3 py-1.5 sm:py-2 rounded-sm text-[0.7rem] sm:text-xs font-display font-semibold bg-cinema-gold text-black hover:bg-cinema-amber transition-colors flex items-center gap-1 shadow-sm whitespace-nowrap"
        >
          <span>Apply</span>
          <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </Link>
      </div>
    </div>
  )
}

// ─── Program Detail Modal ──────────────────────────────────────────────────

function ProgramDetailModal({ course, onClose, onDownload, isDownloading, onDownloadPNG, isDownloadingPNG, onCopyLink, copied }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2.5 sm:p-6 bg-black/90 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl rounded-sm bg-cinema-charcoal border border-white/15 my-2 sm:my-8 overflow-hidden shadow-2xl animate-scale-in max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-30 p-1.5 sm:p-2 rounded-full bg-black/80 hover:bg-black text-white/80 hover:text-white border border-white/20 transition-colors shadow-lg"
          aria-label="Close modal"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* Header */}
        <div className="px-5 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-white/10">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="text-xs uppercase tracking-[0.16em] px-2.5 py-1 rounded bg-cinema-gold text-black font-bold font-sans">
              {course.category}
            </span>
            {course.level && (
              <span className="text-xs uppercase tracking-wider px-2.5 py-1 rounded bg-white/5 text-white border border-white/10 font-sans font-semibold">
                {course.level}
              </span>
            )}
            {course.code && (
              <span className="text-xs font-mono px-2.5 py-1 rounded bg-white/5 text-cinema-gold border border-white/10 font-medium">
                {course.code}
              </span>
            )}
          </div>
          <h2 className="font-display text-xl sm:text-2xl text-white font-semibold leading-tight">
            {course.title}
          </h2>
        </div>

        {/* Body — scrollable */}
        <div className="p-5 sm:p-8 space-y-5 sm:space-y-6 overflow-y-auto touch-pan-y">
          {/* Quick specs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3 font-sans">
            <div className="p-2.5 sm:p-3 rounded-sm bg-white/[0.03] border border-white/5">
              <span className="text-xs uppercase tracking-wider text-cinema-muted block font-medium">Duration</span>
              <span className="text-xs sm:text-sm font-medium text-white flex items-center gap-1.5 mt-0.5">
                <Clock className="w-3.5 h-3.5 text-cinema-gold" />
                {course.duration || '—'}
              </span>
            </div>
            <div className="p-2.5 sm:p-3 rounded-sm bg-white/[0.03] border border-white/5">
              <span className="text-xs uppercase tracking-wider text-cinema-muted block font-medium">Tuition</span>
              <span className="font-display font-medium text-cinema-gold text-xs sm:text-sm mt-0.5 block">
                {course.fee || 'Contact Admissions'}
              </span>
            </div>
            <div className="col-span-2 sm:col-span-1 p-2.5 sm:p-3 rounded-sm bg-white/[0.03] border border-white/5">
              <span className="text-xs uppercase tracking-wider text-cinema-muted block font-medium">Format</span>
              <span className="text-xs sm:text-sm text-white/90 mt-0.5 font-medium">
                100% Practical Studio
              </span>
            </div>
          </div>

          {/* Overview */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-cinema-gold font-sans mb-1.5 font-semibold flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" />
              Course Overview
            </h3>
            <p className="text-xs sm:text-sm text-cinema-soft/90 font-sans leading-relaxed">
              {course.description}
            </p>
          </div>

          {/* Advantages */}
          {course.advantages?.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] text-cinema-gold font-sans mb-2 font-semibold flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                Curriculum Advantages
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {course.advantages.map((adv, i) => (
                  <div key={i} className="p-2.5 rounded-sm bg-white/[0.02] border border-white/5 flex items-start gap-2 text-xs text-cinema-soft font-sans">
                    <CheckCircle2 className="w-3.5 h-3.5 text-cinema-gold shrink-0 mt-0.5" />
                    <span>{adv}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Portfolio deliverables */}
          {course.whatYouCanDo?.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] text-cinema-gold font-sans mb-2 font-semibold flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5" />
                Portfolio Deliverables
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-sans text-cinema-soft">
                {course.whatYouCanDo.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 p-2 sm:p-2.5 rounded-sm bg-white/[0.02] border-l-2 border-cinema-gold/60">
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Career pathways */}
          {course.jobOpportunities?.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] text-cinema-gold font-sans mb-2 font-semibold flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" />
                Career Pathways
              </h3>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {course.jobOpportunities.map((job, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-full text-xs font-sans bg-white/[0.04] border border-white/10 text-white/90 font-medium">
                    {job}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-3 sm:p-5 bg-cinema-black/95 border-t border-white/10 flex flex-wrap items-center justify-between gap-2 shrink-0">
          <button
            type="button"
            onClick={onCopyLink}
            className="px-3 py-1.5 text-[0.7rem] sm:text-xs font-sans text-cinema-muted hover:text-white bg-white/[0.04] hover:bg-white/[0.08] rounded-sm transition-colors flex items-center gap-1"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400">Copied</span>
              </>
            ) : (
              <>
                <Share2 className="w-3.5 h-3.5" />
                <span>Share</span>
              </>
            )}
          </button>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <span className="text-[0.7rem] sm:text-xs font-sans font-semibold text-cinema-soft mr-0.5">Download</span>
            <button
              type="button"
              onClick={onDownload}
              disabled={isDownloading}
              className="px-3.5 py-2 text-[0.7rem] sm:text-xs font-sans text-cinema-gold hover:text-white bg-cinema-gold/10 hover:bg-cinema-gold/20 border border-cinema-gold/30 rounded-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {isDownloading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              <span>PDF</span>
            </button>
            <button
              type="button"
              onClick={onDownloadPNG}
              disabled={isDownloadingPNG}
              className="px-3.5 py-2 text-[0.7rem] sm:text-xs font-sans text-cinema-soft hover:text-white bg-white/5 hover:bg-white/10 border border-white/15 rounded-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {isDownloadingPNG ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileImage className="w-3.5 h-3.5" />
              )}
              <span>PNG</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="cinema-btn-ghost !text-xs !py-2 !px-4"
            >
              Close
            </button>
            <Link
              to={`/register?course=${course.code}`}
              className="cinema-btn-primary !text-xs !py-2 !px-4 flex items-center gap-1.5"
            >
              <span>Apply Now</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
